import {useEffect, useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {InsurancePaymentsTab} from './policyFormTabs/InsurancePaymentsTab'
import {TFormAllValues} from '../../policies/policiesTypes'
import {FormikErrors, FormikTouched} from 'formik'
import {NodePolicy} from '../../../types/policiesApi'
import {STATUS_POLICY} from '../../../helpers/consts'
import {PolicyHolderTab} from './policyFormTabs/PolicyHolderTab'
import {FamilyMembersTab} from './policyFormTabs/FamilyMembersTab'
import {PolicyInsuranceTab} from './policyFormTabs/PolicyInsuranceTab'
import {ExtraTab} from './policyFormTabs/ExtraTab'
import {IResStates} from '../../../types/statesEU'
import {TFormTabsPolicy} from '../soldTypes'
import {IResCarriersList} from '../../../types/carriers'
import {useGetUser} from '../../../store/userStore'

const FormsTabs: {id: TFormTabsPolicy; num: number; name: string; icon?: string}[] = [
  {id: 'policy-holder', num: 1, name: 'Policy holder', icon: 'bi-person'},
  {id: 'members', num: 2, name: 'Members', icon: 'bi-people'},
  {id: 'policy-insurance', num: 3, name: 'Policy insurance', icon: 'bi-file-earmark-text'},
  {id: 'payment', num: 4, name: 'Payment', icon: 'bi-credit-card'},
  {id: 'extras', num: 5, name: 'Extras', icon: 'bi-stars'},
]

const tabLabels = defineMessages({
  'policy-holder': {id: 'policies.detail.form.tab.policyHolder', defaultMessage: 'Policy holder'},
  members: {id: 'policies.detail.form.tab.members', defaultMessage: 'Members'},
  'policy-insurance': {
    id: 'policies.detail.form.tab.policyInsurance',
    defaultMessage: 'Policy insurance',
  },
  payment: {id: 'policies.detail.form.tab.payment', defaultMessage: 'Payment'},
  extras: {id: 'policies.detail.form.tab.extras', defaultMessage: 'Extras'},
})

interface IPolicyProps {
  values: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  handleChange: (field: string, value: string | boolean | null) => void
  handleBlur: (field: string) => void
  handleSubmit: () => void
  addMember: () => void
  removeMember: (index: number) => void
  loadingCreate: boolean
  loadingCarriers: boolean
  dataPolicy: NodePolicy
  refetch: () => void
  dataStates: IResStates | undefined
  dataCarriers: IResCarriersList | undefined
  updateZips?: number
  tabsUpdate?: TFormTabsPolicy,
  onSwitchWithHolder: (index: number) => void,
  canSwitchHolder: boolean,
  category?: string | null
  policyId?: string
}

export const PolicyForm: React.FC<IPolicyProps> = ({
  values,
  handleBlur,
  handleChange,
  handleSubmit,
  addMember,
  removeMember,
  errors,
  touched,
  loadingCreate,
  loadingCarriers,
  dataPolicy,
  refetch,
  dataStates,
  dataCarriers,
  updateZips,
  tabsUpdate,
  onSwitchWithHolder,
  canSwitchHolder,
  category,
  policyId
}) => {
  const [formTab, setFormTab] = useState<TFormTabsPolicy>('policy-holder')
  const intl = useIntl()

  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isAsesor = userGroups.includes('Asesor')
  // Asesores can't edit contact info (email/phone) once the policy is completed.
  const isContactEditDisabled = isAsesor && category === STATUS_POLICY.COMPLETED

  useEffect(() => {
    if (tabsUpdate) setFormTab(tabsUpdate)
  }, [tabsUpdate])

  return (
    <div>
      <div className='policy-form-tabs'>
        {FormsTabs.map((ft) => (
          <div
            onClick={() => {
              if (values.lead && Number(values.lead)) return
              setFormTab(ft.id)
            }}
            key={ft.num}
            className={ft.id === formTab ? 'tab active' : 'tab'}
            style={{flex: 1}}
          >
            <button key={ft.num} className='btn btn-tabs' disabled={ft.id === formTab}>
              <span className='policy-form-tabs-number'>{ft.num}</span>
              <span style={{color: ft.name === formTab ? '#5d78ff' : 'gray'}}>
                {intl.formatMessage(tabLabels[ft.id])}
              </span>
            </button>
          </div>
        ))}
      </div>
      <div className='policy-form-tabs-container'>
        {formTab === 'policy-holder' && (
          <PolicyHolderTab
            onNextStep={() => setFormTab('members')}
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            isEdit={category !== STATUS_POLICY.PENDING}
            updateZips={updateZips}
            dataStates={dataStates}
            disableHolderEmail={isContactEditDisabled}
            disableHolderPhone={isContactEditDisabled}
          />
        )}
        {formTab === 'members' && (
          <FamilyMembersTab
            onNextStep={() => setFormTab('policy-insurance')}
            onPrevStep={() => setFormTab('policy-holder')}
            allValues={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            addMember={addMember}
            removeMember={removeMember}
            loadingCreate={loadingCreate}
            isDisableEdit={category !== STATUS_POLICY.PENDING}
            onSwitchWithHolder={onSwitchWithHolder}
            canSwitchHolder={canSwitchHolder}
          />
        )}
        {formTab === 'policy-insurance' && (
          <PolicyInsuranceTab
            onPrevStep={() => setFormTab('members')}
            onNextStep={() => setFormTab('payment')}
            allValues={values}
            handleBlur={handleBlur}
            handleChange={handleChange}
            errors={errors}
            touched={touched}
            dataCarriers={dataCarriers}
            dataStates={dataStates}
            loadingCarriers={loadingCarriers}
            loadingCreate={loadingCreate}
            isEdit={category !== STATUS_POLICY.PENDING}
          />
        )}
        {formTab === 'payment' && (
          <InsurancePaymentsTab
            onNextStep={() => setFormTab('extras')}
            onPrevStep={() => setFormTab('policy-insurance')}
            handleChange={(field, value) => handleChange(`policyPayment.${field}`, value)}
            handleBlur={(field) => handleBlur(`policyPayment.${field}`)}
            // handleChange={handleChange}
            // handleBlur={handleBlur}
            allValues={values.policyPayment}
            errors={errors}
            touched={touched}
            policyId={policyId}
          />
        )}
        {formTab === 'extras' && (
          <ExtraTab
            onPrevStep={() => setFormTab('payment')}
            handleSubmit={handleSubmit}
            handleChange={(field, value) => handleChange(`policyExtraInfo.${field}`, value)}
            handleBlur={(field) => handleBlur(`policyExtraInfo.${field}`)}
            allValues={values.policyExtraInfo}
            errors={errors}
            touched={touched}
            policyValues={{
              hasAutoPolicy: values.hasAutoPolicy,
              hasHomePolicy: values.hasHomePolicy,
              hasCarrierAccount: values.hasCarrierAccount,
              accountUsername: values.accountUsername,
              accountPassword: values.accountPassword,
              accountCreatedByClient: values.accountCreatedByClient,
            }}
            onPolicyChange={(field, value) => handleChange(field, value)}
            isDisableEdit={category !== STATUS_POLICY.PENDING}
          />
        )}
      </div>
    </div>
  )
}
